/* Verbatim port of renderEntity from service/review/index.html
 * (~2300-2429) — §2 Knowledge Panel & entity.
 *
 * PARAMETERIZED (rule a): reads the global `STATE` (STATE.wikidata,
 * STATE.knowledgePanel, STATE.entity, STATE.brand, STATE.linkGraph,
 * STATE.conflicts via entityHeadline) — takes `state: AppPayloadState` as
 * an explicit first parameter.
 *
 * `${WS.ICON.down}` (three occurrences: the QuickStatements button and
 * both sameAs-export buttons) is a verbatim bare global reference — same
 * treatment as views/exec.ts/views/conflicts.ts (state/workspace.ts's
 * `declare global` block). F2a-F2c ported this file against a temporary
 * `${ICON_DOWN}` byte-identical stand-in constant (ui/exports.ts) because
 * WS didn't exist as a runtime value yet and this template always
 * executes (not availability-gated like exec.ts's WS references); F2d
 * lands the real module, so the stand-in is gone — see ui/exports.ts.
 *
 * F3: onclick="exportQuickStatements()" / onclick="exportSameAs()" convert
 * to `@click` calling the imported functions directly (ui/exports.ts) —
 * this is the ONLY template in the whole codebase that referenced either
 * global as a literal onclick="" string (grepped), so main.ts's
 * `exportQuickStatements`/`exportSameAs` window-shim entries are deleted
 * (see the F3a report's checklist).
 *
 * UNSAFE-HTML AMENDMENT — RETIRED (F3c): the three `${WS.ICON.down}` sites
 * below used `unsafeHTML` from F3a through the WS.ICON conversion commit
 * (state/workspace.ts's `ICON` dictionary was a raw SVG-markup string
 * dictionary until then). Now that `ICON` is a real `svg\`\`` TemplateResult
 * dictionary, these are plain lit child bindings — the amendment to
 * convention #11 this file's F3a report flagged for sign-off no longer
 * applies; nothing in this codebase needs the markdown-only unsafeHTML
 * exception to be broadened anymore.
 *
 * DEVIATION: the original destructured `const wd = STATE.wikidata, kp =
 * STATE.knowledgePanel, ent = STATE.entity;` (~2303) but never read `ent`
 * anywhere in the function body — a pure dead local, same class of
 * deviation as ui/popover.ts's dropped `others`. Dropped here (TS
 * `noUnusedLocals`); behavior-neutral.
 */
import { html, nothing, render } from 'lit';
import { join } from 'lit/directives/join.js';
import { entityHeadline } from '../derive/entity.ts';
import { domainOf, fixMojibake } from '../lib/format.ts';
import { chipT, tipT } from '../lib/templates.ts';
import { openDrawer } from '../ui/drawer.ts';
import { exportQuickStatements, exportSameAs } from '../ui/exports.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export function renderEntity(state: AppPayloadState): void {
  const el = document.getElementById('entityBody')!;
  const eh = entityHeadline(state);
  const wd = state.wikidata, kp = state.knowledgePanel;
  if (!eh){ render(html`<div class="why">No entity data in this run — the seed step resolves the Knowledge Graph and Wikidata.</div>`, el); return; }

  // built as TemplateResults (not strings) from the start — their <b> tags
  // are static template markup, not dynamic content, so no unsafeHTML is
  // needed here (unlike the WS.ICON.down sites below, which really are
  // dynamic raw-HTML values).
  const kpWord = eh.kpClaimed === 'yes' ? html`the panel is <b>claimed</b>`
    : eh.kpClaimed === 'unknown' ? html`the panel's <b>claim status could not be verified</b>`
    : html`the panel is <b>not claimed</b>`;
  const chainClause = eh.measured && eh.bridges
    ? html` — and the link crawl found <b>${eh.bridges} missing cross-links to build</b>${eh.orphans ? html` (${eh.orphans} client properties are complete orphans — nothing observed points at them)` : nothing}`
    : eh.weak ? html` — and ${eh.weak} official profiles have no authoritative cross-link proving they belong to the client` : nothing;
  const finding = eh.kpPresent
    ? html`${state.brand} has a Knowledge Panel and a Wikidata item${eh.qid ? ` (${eh.qid})` : ''}, but ${kpWord} and <b>${eh.fix} of ${eh.total} Wikidata claims need fixing or adding</b>${chainClause}.`
    : html`${state.brand} has <b>no Knowledge Panel yet</b> — the worksheet below is the creation payload.`;

  render(html`
    <div class="finding">${finding}</div>
    <div class="why"><b>Why this matters:</b> the ${tipT('Knowledge Panel','Knowledge Panel')} is the box Google shows beside a branded search — for many searchers it IS the brand's identity card. Google builds it from ${tipT('Wikidata','Wikidata')}, Wikipedia, and the cross-links between the client's own site and its profiles. Fixing the facts at the source fixes them everywhere, including in AI answers.</div>

    <div class="cardrow cols-2">
      <div class="card kgcard" id="kpCard"></div>
      <div class="card kgcard" id="wdCard"></div>
    </div>

    <div class="card" style="margin-bottom:16px">
      <h3>Does Google believe these profiles are really ${state.brand}'s?</h3>
      <div class="sub">a cohesive entity means the site, Wikidata, and every official profile point at each other</div>
      <div id="chainStory"></div>
    </div>

    <div style="display:flex;align-items:baseline;gap:14px;margin-bottom:8px">
      <h3 style="font-size:16px">The Wikidata worksheet</h3>
      <div class="wtally" id="wsTally" style="margin:0"></div>
      <button class="exportbtn" style="margin-left:auto" @click=${() => exportQuickStatements(state)} title="Export the missing/stale claims as a QuickStatements batch you can paste straight into Wikidata">${WS.ICON.down}QuickStatements export</button>
    </div>
    <div class="wsheet"><table class="ws">
      <thead><tr><th>Property</th><th>On Wikidata now</th><th>From our research (with source)</th><th>State</th></tr></thead>
      <tbody id="wsRows"></tbody>
    </table></div>`, el);

  // KP card
  render(html`
    <h4>Google Knowledge Panel</h4>
    ${kp ? html`<div class="big">${kp.present ? 'Present' : 'Not present'} ${kp.present ? chipT(kp.claimed === 'yes' ? 'claimed' : kp.claimed === 'unknown' ? 'claim status unknown' : 'not claimed', kp.claimed === 'yes' ? 'ok' : 'warn') : nothing}</div>
      <dl class="kv2">
        <dt>Descriptor</dt><dd>${kp.descriptor || '—'}</dd>
        <dt>Social links shown</dt><dd>${(kp.socialLinks||[]).length ? join((kp.socialLinks||[]).map((u) => {
          const d = domainOf(u); if (!d) return u;
          const handle = (u.replace(/\/+$/,'').split('/').pop() || '').replace(/^@?/,'');
          const label = handle && !/^(user|company|wiki)$/.test(handle) && handle.length < 28 ? `${d.split('.')[0]}/${handle}` : d;
          return html`<a href="${u}" target="_blank" rel="noopener" style="color:var(--azure)">${label}</a>`;
        }), ' · ') : '—'}</dd>
      </dl>
      ${(kp.issues && kp.issues.length) ? html`<div style="font-size:11.5px;font-weight:600;color:var(--muted);margin-top:12px">What to fix on the panel</div><ul>${kp.issues.map((i) => html`<li>${i}</li>`)}</ul>` : nothing}`
    : html`<div class="big">Not resolved this run</div>`}`, document.getElementById('kpCard')!);

  // Wikidata card
  const create = wd && (wd.createCandidate || !wd.present);
  render(html`
    <h4>Wikidata item ${create ? chipT('create mode','warn') : nothing}</h4>
    ${wd ? html`<div class="big">${wd.present ? (wd.label || wd.qid) : 'No item — creation candidate'}</div>
      <dl class="kv2">
        ${wd.present ? html`<dt>Item</dt><dd><a href="${wd.itemUrl}" target="_blank" rel="noopener" style="color:var(--azure)">${wd.qid}</a></dd>` : nothing}
        ${wd.description ? html`<dt>Description</dt><dd>${wd.description}</dd>` : nothing}
        <dt>Claims reviewed</dt><dd>${(wd.worksheet||[]).length} — ${eh.confirmed} confirmed, ${eh.fix} to fix or add</dd>
      </dl>
      <div style="font-size:12px;color:var(--ink-2);margin-top:12px;line-height:1.55">${create
        ? 'No Wikidata item exists — each worksheet row below is a sourced claim ready to seed a new item, the first step toward a Knowledge Panel.'
        : 'The worksheet below diffs the live item against our verified research: what\'s confirmed, what\'s missing, what\'s gone stale.'}</div>` : html`<div class="big">Not resolved</div>`}`, document.getElementById('wdCard')!);

  // cross-linking story: the measured bridge worklist (falls back to the old
  // weak-chains summary for payloads that predate the link crawl)
  const lg = state.linkGraph;
  const bridges = (lg && lg.bridges) || [];
  const weak = (lg && lg.weakChains) || [];
  const orphans = (lg && lg.orphans) || [];
  const chainEl = document.getElementById('chainStory')!;
  const MISSING_LABEL: Record<string, [string, string]> = {
    'site-sameas':['no sameAs on the site','warn'], 'wikidata':['not on Wikidata','warn'], // R11: missing cross-link = build-work, not adverse
    'site-link':['no site link','warn'], 'backlink':['no backlink to the site','warn'],
    'site-sameas-unverified':['sameAs unverified','pub'], 'backlink-unverifiable':['backlink unverifiable (platform blocks bots)','pub'] };
  if (bridges.length){
    const noSameAs = lg?.siteReadable && (lg?.sameAsFound === 0);
    render(html`
      ${noSameAs ? html`<div class="conflict" style="margin-bottom:12px"><p><b>The site publishes no sameAs markup at all.</b> Every official profile is unanchored on the site side — Google and AI engines are left to infer which accounts are genuinely ${state.brand}'s. The export below fixes the entire list in one snippet.</p></div>` : nothing}
      <p style="font-size:13.5px;line-height:1.6;margin-bottom:10px">The crawler read the site${lg?.renderedUsed ? ' (rendered — the static page is bot-gated)' : ''}, the full Wikidata identifier set (${lg?.wikidataUrls || 0} linked URLs), and ${(lg && lg.pageFacts && lg.pageFacts.fetched) || 0} of the mapped pages themselves. <b>${bridges.length} cross-links are missing</b>, ranked below by how much each would strengthen the entity${orphans.length ? `; ${orphans.length} client properties are complete orphans — no observed link in either direction` : ''}.</p>
      <table class="tbl"><thead><tr><th>Property / profile</th><th>What's missing</th><th>The fix</th></tr></thead>
      <tbody>${bridges.slice(0, 8).map((b) => html`
        <tr class="click" data-id="${b.assetId}" @click=${() => openDrawer(state, b.assetId)}>
          <td style="white-space:nowrap"><b>${fixMojibake(b.title)}</b></td>
          <td><div class="chiprow">${b.missing.map((m) => { const [l, c] = MISSING_LABEL[m] || [m,'']; return chipT(l, c); })}</div></td>
          <td style="font-size:12px;color:var(--ink-2);line-height:1.5">${b.fixes.join(' · ')}</td>
        </tr>`)}</tbody></table>
      ${bridges.length > 8 ? html`<details style="margin-top:8px"><summary style="cursor:pointer;font-size:12px;color:var(--azure);font-weight:600">Show all ${bridges.length} missing links</summary>
        <div style="font-size:12.5px;line-height:1.9;margin-top:8px;color:var(--ink-2)">${join(bridges.slice(8).map((b) => `${fixMojibake(b.title)} — ${b.missing.map((m) => (MISSING_LABEL[m]||[m])[0]).join(', ')}`), html`<br>`)}</div></details>` : nothing}
      ${orphans.length ? html`<details style="margin-top:8px"><summary style="cursor:pointer;font-size:12px;color:var(--neg);font-weight:600">Show the ${orphans.length} orphaned properties</summary>
        <div style="font-size:12.5px;line-height:1.9;margin-top:8px;color:var(--ink-2)">${join(orphans.map((o) => fixMojibake(o.title)), html`<br>`)}</div></details>` : nothing}
      <div style="margin-top:14px"><button class="exportbtn" @click=${() => exportSameAs(state)} title="Generates the schema.org sameAs JSON-LD block for the client's web team">${WS.ICON.down}sameAs block for the web team</button>
      <span style="font-size:11.5px;color:var(--muted);margin-left:10px">one copy-paste snippet that closes every "no sameAs" gap above</span></div>`, chainEl);
  } else if (weak.length){
    const couldntRead = lg?.siteReadable === false;
    render(html`
      <p style="font-size:13.5px;line-height:1.6;margin-bottom:10px"><b>${weak.length} official profiles have no explicit cross-reference</b> — neither the client's website (via ${tipT('sameAs','sameAs markup')}) nor Wikidata currently points to them.
      ${couldntRead ? html`<span style="color:var(--amber-tx)"> Caveat: the site could not be read this pass — these are <b>unverified, not confirmed orphans</b>.</span>` : nothing}</p>
      <details><summary style="cursor:pointer;font-size:12px;color:var(--azure);font-weight:600">Show the ${weak.length} profiles</summary>
        <div style="font-size:12.5px;line-height:1.9;margin-top:8px;color:var(--ink-2)">${join(weak.map((w) => fixMojibake(w.title)), html`<br>`)}</div></details>
      <div style="margin-top:12px"><button class="exportbtn" @click=${() => exportSameAs(state)}>${WS.ICON.down}sameAs block for the web team</button></div>`, chainEl);
  } else {
    render(html`<p style="font-size:13.5px;color:var(--ink-2)">All mapped official profiles are cross-referenced by the site or Wikidata — the entity reads as cohesive.</p>`, chainEl);
  }

  // worksheet
  const ws = (wd && wd.worksheet) || [];
  const cnt = (s: string) => ws.filter((w) => w.state === s).length;
  render(html`<span><i style="background:var(--green-tx)"></i>${cnt('confirmed')} confirmed</span>
     <span><i style="background:var(--amber)"></i>${cnt('missing')} to add</span>
     <span><i style="background:var(--neg)"></i>${cnt('stale')} to fix</span>`, document.getElementById('wsTally')!);
  render(html`${ws.map((w) => html`
    <tr>
      <td><div class="prop">${w.property}</div><div class="pc">${w.pcode}</div></td>
      <td class="cur ${w.current ? '' : 'none'}">${w.current ? w.current : '— not set'}</td>
      <td class="res">${w.research || '—'}
        ${w.source ? html`<span class="src"> <a href="${w.source}" target="_blank" rel="noopener">${domainOf(w.source) || w.source} ↗</a></span>` : nothing}
        ${w.note ? html`<div class="wnote">${w.note}</div>` : nothing}</td>
      <td><span class="wstate ${w.state}">${w.state === 'confirmed' ? 'Confirmed' : w.state === 'missing' ? 'Add' : 'Fix'}</span></td>
    </tr>`)}`, document.getElementById('wsRows')!);
}
